import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY, supabase } from "@/lib/supabase";

/**
 * Treff fra brreg-search edge function. Speiler shape-en backend mapper til
 * (se supabase/functions/brreg-search/index.ts).
 */
export interface BrregHit {
  organisasjonsnummer: string;
  navn: string;
  adresse: string | null;
  postnummer: string | null;
  poststed: string | null;
  /** Hjemmesiden slik registeret har den, ofte uten https:// («www.firma.no»). */
  hjemmeside: string | null;
  /** Til «Nytt lead»: næringskode, kommune og ansatte fra registeret. */
  naeringskode?: string | null;
  naeringsbeskrivelse?: string | null;
  kommune?: string | null;
  antall_ansatte?: number | null;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * Debounce-wrapper rundt en string-verdi. Brukes til å unngå å skyte mange
 * brreg-kall mens brukeren fortsatt skriver.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

async function fetchBrregHits(query: string): Promise<BrregHit[]> {
  // Send med innloggings-JWT slik at edge function kan rate-limite per bruker
  // (ikke per IP — hele Sølvrev-kontoret deler IP).
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token ?? SUPABASE_ANON_KEY;

  const url = `${SUPABASE_FUNCTIONS_URL}/brreg-search?navn=${encodeURIComponent(query)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_ANON_KEY,
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Brreg-søk feilet (${res.status}): ${text.slice(0, 200)}`);
  }
  const data = (await res.json()) as { hits?: BrregHit[] };
  return data.hits ?? [];
}

/**
 * Søker i Brønnøysundregisteret via vår proxy-edge function. Debouncer 300ms
 * og caller ikke under 2 tegn — så får ikke Brreg en haug med småsøk mens
 * brukeren skriver.
 */
export function useBrregSearch(query: string) {
  const debounced = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY_LENGTH;

  return useQuery({
    queryKey: ["brreg-search", debounced],
    queryFn: () => fetchBrregHits(debounced),
    enabled,
    staleTime: 60_000,
    retry: 0,
  });
}
